import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Row, Col, Button, Form, InputGroup, ButtonGroup, Badge } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faPlus, 
  faSearch,
  faCalendarAlt,
  faCircle,
  faCheckCircle,
  faTimesCircle,
  faHandshake,
  faBoxes,
  faEye,
  faTimes
} from '@fortawesome/free-solid-svg-icons';
import DatePicker from 'react-datepicker';
import { registerLocale, setDefaultLocale } from 'react-datepicker';
import enUS from 'date-fns/locale/en-US';
import itIT from 'date-fns/locale/it';
import zhCN from 'date-fns/locale/zh-CN';
import esES from 'date-fns/locale/es';
import "react-datepicker/dist/react-datepicker.css";
import DeviceTable from './DeviceTable';
import DeviceForm from './DeviceForm';
import DeviceDetails from './DeviceDetails';
import './Dashboard.scss';
import { deviceService } from '../../services/firestore';
import { auth } from '../../services/firebase';
import SearchBar from './SearchBar';
import logoIcon from '../../assets/images/logo-icon.png';
import { useTranslation } from 'react-i18next';

// Registra le localizzazioni
registerLocale('en', enUS);
registerLocale('it', itIT);
registerLocale('zh', zhCN);
registerLocale('es', esES);

function Dashboard() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [showDeviceForm, setShowDeviceForm] = useState(false);
  const [showDeviceDetails, setShowDeviceDetails] = useState(false);
  const [devices, setDevices] = useState([]);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    startDate: null,
    endDate: null
  });
  const [lastDoc, setLastDoc] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [prefill, setPrefill] = useState(null);

  // Aggiorna il DatePicker per utilizzare la lingua corrente
  const currentLocale = (() => {
    switch (i18n.language) {
      case 'it': return itIT;
      case 'zh': return zhCN;
      case 'es': return esES;
      default: return enUS;
    }
  })();

  useEffect(() => {
    fetchDevices();
  }, [filters]);

  // Apre il form con i dati importati dal tool Preventivo
  useEffect(() => {
    if (location.state?.openDeviceForm && location.state?.prefill) {
      setSelectedDevice(null);
      setPrefill(location.state.prefill);
      setShowDeviceForm(true);
      navigate(location.pathname, { replace: true });
    }
  }, [location, navigate]);

  const fetchDevices = async (isLoadMore = false) => {
    try {
      setLoading(true);
      const result = await deviceService.getAll(
        filters,
        isLoadMore ? lastDoc : null
      );

      setDevices(prev => isLoadMore ? [...prev, ...result.devices] : result.devices);
      setLastDoc(result.lastDoc);
      setHasMore(result.hasMore);
    } catch (error) {
      console.error('Error fetching devices:', error);
      handleFetchError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeviceCreate = async (deviceData) => {
    try {
      const newDevice = await deviceService.create({
        ...deviceData,
        userId: auth.currentUser.uid,
        entryDate: new Date()
      });
      setDevices(prev => [newDevice, ...prev]);
      return newDevice;
    } catch (error) {
      console.error('Error creating device:', error);
      throw error;
    }
  };

  const statusOptions = [
    { value: '', label: t('dashboard.filters.status.all'), icon: null, color: '#6c757d' },
    { value: 'inRiparazione', label: t('dashboard.filters.status.inRepair'), icon: faCircle, color: '#ffc107' },
    { value: 'riparato', label: t('dashboard.filters.status.repaired'), icon: faCheckCircle, color: '#0dcaf0' },
    { value: 'nonRiparabile', label: t('dashboard.filters.status.notRepairable'), icon: faTimesCircle, color: '#dc3545' },
    { value: 'ordine', label: t('dashboard.filters.status.order'), icon: faBoxes, color: '#007bff' }
  ];

  const handleDeviceSelect = (device) => {
    setSelectedDevice(device);
    setShowDeviceDetails(true);
  };

  const handleSearch = (value) => {
    setFilters(prev => ({ 
      ...prev, 
      search: value
    }));
  };

  useEffect(() => {
    // Se la ricerca non produce risultati e ci sono più dati da caricare, carica automaticamente
    if (devices.length === 0 && hasMore && !loading) {
      fetchDevices(true);
    }
  }, [devices.length, hasMore, loading]);

  const handleStatusFilter = (status) => {
    setFilters(prev => ({ 
      ...prev, 
      status: prev.status === status ? '' : status 
    }));
  };

  const handleDateChange = (dates) => {
    const [start, end] = dates;
    setFilters(prev => ({
      ...prev,
      startDate: start,
      endDate: end
    }));
  };

  const handleDeviceUpdate = async (updatedDevice) => {
    try {
      await deviceService.update(updatedDevice.id, updatedDevice);
      await fetchDevices(); // Ricarica la lista
    } catch (error) {
      console.error('Error updating device:', error);
    }
  };

  const handleDeviceDelete = async (deviceId) => {
    try {
      await deviceService.delete(deviceId);
      await fetchDevices();
    } catch (error) {
      console.error('Error deleting device:', error);
    }
  };

  const handleLoadMore = () => {
    fetchDevices(true);
  };

  const columns = [
    { key: 'repairNumber', label: t('dashboard.table.columns.repairNumber') },
    { key: 'brand', label: t('dashboard.table.columns.brand') },
    { key: 'model', label: t('dashboard.table.columns.model') },
    { key: 'problem', label: t('dashboard.table.columns.problem') },
    { key: 'entryDate', label: t('dashboard.table.columns.entryDate') },
    { key: 'deliveryDate', label: t('dashboard.table.columns.deliveryDate') },
    { key: 'status', label: t('dashboard.table.columns.status') },
    { key: 'paymentStatus', label: t('dashboard.table.columns.paymentStatus') },
    { key: 'actions', label: t('dashboard.table.columns.actions') }
  ];

  const handleFetchError = (error) => {
    if (error.message?.includes('requires an index')) {
      console.log(t('dashboard.errors.fetchDevices.indexRequired'));
      setFilters(prev => ({
        ...prev,
        status: ''
      }));
    } else if (error.code === 'failed-precondition') {
      console.log(t('dashboard.errors.fetchDevices.dbConfig'));
    } else {
      console.log(t('dashboard.errors.fetchDevices.generic'));
    }
  };

  return (
    <>
      <Row className="mb-4 align-items-center">
        <Col className="d-flex align-items-center">
          <img 
            src={logoIcon} 
            alt="PhoneDesk Logo" 
            style={{ height: '60px', width: 'auto' }}
          />
          <h1 className="mb-0">{t('dashboard.title')}</h1>
        </Col>
        <Col xs="auto">
          <Button
            variant="primary"
            onClick={() => {
              setSelectedDevice(null);
              setPrefill(null);
              setShowDeviceForm(true);
            }}
          >
            <FontAwesomeIcon icon={faPlus} className="me-2" />
            {t('dashboard.newRepair')}
          </Button>
        </Col>
      </Row>

      <div className="filters-container mb-4">
        <Row className="align-items-center">
          {/* Status Filter Buttons */}
          <Col md="auto" className="flex-grow-0">
            <div className="status-buttons">
              {statusOptions.map(option => (
                <Button
                  key={option.value}
                  variant={filters.status === option.value ? 'primary' : 'light'}
                  onClick={() => handleStatusFilter(option.value)}
                  className="status-button"
                >
                  {option.icon && (
                    <FontAwesomeIcon 
                      icon={option.icon} 
                      className="me-2"
                      style={{ color: filters.status === option.value ? 'white' : option.color }}
                    />
                  )}
                  {option.label}
                </Button>
              ))}
            </div>
          </Col>

          {/* Search Bar */}
          <Col>
            <SearchBar 
              value={filters.search}
              onSearch={handleSearch}
            />
          </Col>

          {/* Date Filter */}
          <Col md={3}>
            <div className="date-picker-container">
              <FontAwesomeIcon icon={faCalendarAlt} className="date-picker-icon" />
              <DatePicker
                selectsRange
                startDate={filters.startDate}
                endDate={filters.endDate}
                onChange={handleDateChange}
                dateFormat="dd/MM/yyyy"
                placeholderText={t('dashboard.filters.date')}
                className="form-control w-100"
                isClearable
                locale={currentLocale}
                monthsShown={1}
                showMonthDropdown
                showYearDropdown
                dropdownMode="select"
                popperClassName="datepicker-improved"
                popperProps={{
                  positionFixed: true,
                  modifiers: [
                    {
                      name: 'offset',
                      options: {
                        offset: [0, 8]
                      }
                    }
                  ]
                }}
              />
            </div>
          </Col>
        </Row>
      </div>

      <DeviceTable 
        devices={devices}
        onSelectDevice={handleDeviceSelect}
        filters={filters}
        onLoadMore={handleLoadMore}
        hasMore={hasMore}
        loading={loading}
        columns={columns}
      />

      {selectedDevice && (
        <DeviceDetails
          show={showDeviceDetails}
          device={selectedDevice}
          onHide={() => {
            setShowDeviceDetails(false);
            setSelectedDevice(null);
          }}
          onUpdate={handleDeviceUpdate}
          onDelete={handleDeviceDelete}
        />
      )}

      <DeviceForm
        show={showDeviceForm}
        device={selectedDevice}
        initialValues={prefill}
        onHide={() => {
          setShowDeviceForm(false);
          setSelectedDevice(null);
          setPrefill(null);
        }}
        onSave={async (deviceData) => {
          try {
            if (selectedDevice) {
              await deviceService.update(selectedDevice.id, deviceData);
            } else {
              await handleDeviceCreate(deviceData);
            }
            await fetchDevices();
            setShowDeviceForm(false);
            setSelectedDevice(null);
          } catch (error) {
            console.error('Error saving device:', error);
          }
        }}
        initialReceiptNumber={devices.length > 0 ? Math.max(...devices.map(d => d.repairNumber || 0)) + 1 : 1}
      />
    </>
  );
}

export default Dashboard; 